import {
  formatDateTime,
  formatHours,
  isProjectOpen,
  type ProjectOption,
  ProjectStatuses,
  type Resource,
  ReviewStatuses,
  routes,
  type Worklog,
  type WorklogAction,
} from '@pmai/contracts';
import { Alert, Button, InlineSelect, InlineTextField, StatusBadge } from '@pmai/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JiraKey } from '../../entities/jira/jira-links';
import { WORKLOG_VIEWS } from '../../entities/jira/worklog-views';
import { useApiMutation } from '../../shared/api/hooks';
import { useEnumLabel, useErrorText } from '../../shared/i18n';

/**
 * One worklog (FR-JIRA-015): who, when, how long, the Jira issue; map it to a project and a
 * resource and save, approve or reject (FR-JIRA-018…021).
 */
export function WorklogCard({
  worklog: w,
  projects,
  resources,
  baseUrl,
}: {
  worklog: Worklog;
  projects: readonly ProjectOption[];
  resources: readonly Resource[];
  baseUrl: string | null;
}) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const errorText = useErrorText();
  const [projectId, setProjectId] = useState(w.projectId ?? '');
  const [resourceId, setResourceId] = useState(w.resourceId ?? '');
  const [note, setNote] = useState(w.reviewNote);
  const review = useApiMutation(routes.worklogs.review, { invalidates: WORKLOG_VIEWS });
  const act = (action: WorklogAction) =>
    review.mutate({ params: { id: w.id }, body: { action, projectId, resourceId, note } });
  const status = ReviewStatuses.byCode(w.reviewStatus);
  const chosen = projects.find((p) => p.id === projectId);
  const closed = chosen && !isProjectOpen(chosen.status) ? chosen : null;

  return (
    <article
      aria-label={`${w.authorName ?? '—'} · ${w.issueKey ?? w.issueId}`}
      data-status={w.reviewStatus}
      className="flex flex-col gap-2.5 rounded-card border border-border bg-surface p-4 shadow-sm"
    >
      {w.flagged && (
        <Alert tone="warning">{t('reconciliation.flaggedBanner', { reason: w.flaggedReason ?? '' })}</Alert>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-semibold">{w.authorName ?? '—'}</span>
          <span className="text-ink-muted"> · {formatDateTime(w.started)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold tabular-nums" data-testid="worklog-hours">
            {formatHours(w.hours)}
          </span>
          <StatusBadge tone={status.tone} label={label(status)} />
        </div>
      </div>
      <div className="text-sm">
        {w.issueKey ? (
          <JiraKey jiraKey={w.issueKey} baseUrl={baseUrl} />
        ) : (
          <span>{t('reconciliation.issueId', { id: w.issueId })}</span>
        )}{' '}
        {w.issueSummary}
        {w.jiraProjectName && <span className="text-ink-muted"> · {w.jiraProjectName}</span>}
      </div>
      {closed && (
        <Alert tone="warning">
          {t('reconciliation.closedProject', {
            name: closed.name,
            status: label(ProjectStatuses.byCode(closed.status)),
          })}
        </Alert>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <InlineSelect
          label={t('reconciliation.project')}
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          data-closed={closed ? true : undefined}
          className={closed ? 'border-amber-500' : undefined}
          options={[
            { value: '', label: t('reconciliation.chooseProject') },
            ...projects.map((p) => ({ value: p.id, label: `${p.name} (${p.customerName})` })),
          ]}
        />
        <InlineSelect
          label={t('reconciliation.resource')}
          value={resourceId}
          onChange={(e) => setResourceId(e.target.value)}
          options={[
            { value: '', label: t('reconciliation.chooseResource') },
            ...resources.map((r) => ({ value: r.id, label: r.name })),
          ]}
        />
        <div className="min-w-48 flex-1">
          <InlineTextField
            label={t('reconciliation.note')}
            placeholder={t('reconciliation.notePlaceholder')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="secondary" disabled={review.isPending} onClick={() => act('save')}>
            {t('common.save')}
          </Button>
          <Button size="sm" disabled={review.isPending} onClick={() => act('approve')}>
            {t('reconciliation.approve')}
          </Button>
          <Button size="sm" variant="ghost" disabled={review.isPending} onClick={() => act('reject')}>
            {t('reconciliation.reject')}
          </Button>
        </div>
      </div>
      {review.error && <Alert tone="danger">{errorText(review.error)}</Alert>}
    </article>
  );
}
